import React from 'react';
import { Box, Step, StepButton, StepLabel, Stepper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { locationApi } from 'components/location/LocationApi';
import { ILocationConfig } from 'components/location/models/LocationModel';
import { IconButton } from 'ui/buttons/IconButton';
import { Column } from 'ui/Flex';
import { buildCreateLocationBody } from './buildCreateLocationBody';
import { LocationGeneralForm, LocationGeneralFormData, generalDefaults } from './forms/LocationGeneralForm';
import { LocationAddressForm, LocationAddressFormData, addressDefaults } from './forms/LocationAddressForm';
import {
    LocationServicesForm,
    LocationServicesFormData,
    servicesDefaults
} from './forms/LocationServicesForm';
import { LocationLinksForm, LocationLinksFormData, linksDefaults } from './forms/LocationLinksForm';
import { LocationImagesForm, LocationImagesFormData, imagesDefaults } from './forms/LocationImagesForm';

const STEPS = ['General', 'Location', 'Services', 'Links', 'Images'];

interface CreateLocationStepperProps {
    onClose?: () => void;
    onError: (message: string) => void;
    onSuccess: (locationTitle: string) => void;
    locationConfig: ILocationConfig;
}

export const CreateLocationStepper: React.FC<CreateLocationStepperProps> = ({
    onClose,
    onError,
    onSuccess,
    locationConfig
}) => {
    const [activeStep, setActiveStep] = React.useState(0);
    const [generalData, setGeneralData] = React.useState<LocationGeneralFormData>(generalDefaults);
    const [addressData, setAddressData] = React.useState<LocationAddressFormData>(addressDefaults);
    const [servicesData, setServicesData] = React.useState<LocationServicesFormData>(servicesDefaults);
    const [linksData, setLinksData] = React.useState<LocationLinksFormData>(linksDefaults);
    const [imagesData, setImagesData] = React.useState<LocationImagesFormData>(imagesDefaults);
    const [isLoading, setIsLoading] = React.useState(false);

    const handleStepClick = React.useCallback(
        (index: number) => () => {
            setActiveStep(index);
        },
        []
    );

    const handleGeneralSubmit = React.useCallback((data: LocationGeneralFormData) => {
        setGeneralData(data);
        setActiveStep(1);
    }, []);

    const handleAddressSubmit = React.useCallback((data: LocationAddressFormData) => {
        setAddressData(data);
        setActiveStep(2);
    }, []);

    const handleServicesSubmit = React.useCallback((data: LocationServicesFormData) => {
        setServicesData(data);
        setActiveStep(3);
    }, []);

    const handleLinksSubmit = React.useCallback((data: LocationLinksFormData) => {
        setLinksData(data);
        setActiveStep(4);
    }, []);
    const handleImagesSubmit = React.useCallback(
        async (images: LocationImagesFormData) => {
            setImagesData(images);
            setIsLoading(true);
            const body = buildCreateLocationBody(generalData, addressData, servicesData, linksData, images);
            const result = await locationApi.create({ body });
            if (!result.ok) {
                setIsLoading(false);
                onError(result.body.message);
            } else {
                onSuccess(generalData.title);
            }
        },
        [generalData, addressData, servicesData, linksData, onError, onSuccess]
    );

    const handlePrevious = React.useCallback(() => {
        setActiveStep(prev => prev - 1);
    }, []);

    return (
        <Box overflow="hidden">
            <Column>
                <Box display="flex" flexDirection="row" pt={4}>
                    <Box flex={1}>
                        <Stepper nonLinear alternativeLabel activeStep={activeStep}>
                            {STEPS.map((item, index) => (
                                <Step key={`step-button-${item}`} completed={activeStep > index}>
                                    <StepButton
                                        disableRipple
                                        disabled={activeStep < index}
                                        onClick={handleStepClick(index)}
                                        color="inherit"
                                    >
                                        <StepLabel>{item}</StepLabel>
                                    </StepButton>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                    <Box position="absolute" top={0} right={0}>
                        <IconButton size="large" onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>
                <LocationGeneralForm
                    open={activeStep === 0}
                    onSubmit={handleGeneralSubmit}
                    initialValues={generalData}
                />
                <LocationAddressForm
                    open={activeStep === 1}
                    onSubmit={handleAddressSubmit}
                    onPrevious={handlePrevious}
                    initialValues={addressData}
                />
                <LocationServicesForm
                    open={activeStep === 2}
                    onSubmit={handleServicesSubmit}
                    onPrevious={handlePrevious}
                    initialValues={servicesData}
                    locationConfig={locationConfig}
                />
                <LocationLinksForm
                    open={activeStep === 3}
                    onSubmit={handleLinksSubmit}
                    onPrevious={handlePrevious}
                    initialValues={linksData}
                />
                <LocationImagesForm
                    open={activeStep === 4}
                    onSubmit={handleImagesSubmit}
                    onPrevious={handlePrevious}
                    initialValues={imagesData}
                    isLoading={isLoading}
                />
            </Column>
        </Box>
    );
};
